import { useState, useRef } from "react";
import styles from "./restaurant-menu.module.css";
import Button from "../common/button/button";
import { AppIcon } from "../common/icon-svg/app-icon.model";
import ButtonIcon from "../common/button-icon/button-icon";

// Define types for menu items
interface MenuItem {
  name: string;
  description: string;
  price: string;
}

// Define props for MenuPanel
interface MenuPanelProps {
  title: string;
  children: React.ReactNode;
  position: "left" | "center" | "right";
}

// Define props for MenuSection
interface MenuSectionProps {
  title: string;
  items: MenuItem[];
}

const MenuPanel: React.FC<MenuPanelProps> = ({ title, children, position }) => {
  return (
    <div
      className={`${styles.menuPanel} ${
        styles[`panel${position.charAt(0).toUpperCase() + position.slice(1)}`]
      }`}
    >
      <div className={styles.panelContent}>
        <div className={styles.panelTitle}>{title}</div>
        {children}
      </div>
    </div>
  );
};

const MenuSection: React.FC<MenuSectionProps> = ({ title, items }) => {
  return (
    <div className={styles.menuSection}>
      <div className={styles.sectionTitle}>{title}</div>
      {items.map((item: MenuItem, index: number) => (
        <div className={styles.menuItem} key={index}>
          <div className={styles.itemInfo}>
            <div className={styles.itemName}>{item.name}</div>
            <div className={styles.itemDescription}>{item.description}</div>
          </div>
          <div className={styles.itemPrice}>{item.price}</div>
        </div>
      ))}
    </div>
  );
};

const RestaurantMenu: React.FC = () => {
  const [isLeftOpen, setIsLeftOpen] = useState(false);
  const [isRightOpen, setIsRightOpen] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  const openMenu = () => {
    if (!isLeftOpen) {
      setIsLeftOpen(true);
      setTimeout(() => {
        setIsRightOpen(true);
        setTimeout(() => setShowCloseButton(true), 600); // Additional delay for the close button
      }, 400); // Delay right panel
    }
  };

  const closeMenu = () => {
    setIsRightOpen(false);
    setShowCloseButton(false);
    setTimeout(() => setIsLeftOpen(false), 400); // Reverse order for closing
  };

  const handlePanelClick = () => {
    if (!isLeftOpen || !isRightOpen) openMenu();
  };

  const appetizerItems: MenuItem[] = [
    {
      name: "Bruschetta Trio",
      description: "Traditional tomato basil, ricotta honey, and tapenade",
      price: "$14",
    },
    {
      name: "Burrata Caprese",
      description: "Fresh burrata, heirloom tomatoes, basil oil",
      price: "$18",
    },
    {
      name: "Arancini",
      description: "Crispy risotto balls with marinara and parmesan",
      price: "$16",
    },
  ];

  const saladItems: MenuItem[] = [
    {
      name: "Caesar Classico",
      description: "Romaine, parmesan, croutons, house-made dressing",
      price: "$12",
    },
    {
      name: "Mediterranean Bowl",
      description: "Mixed greens, olives, feta, cucumber, lemon vinaigrette",
      price: "$15",
    },
  ];

  const pastaItems: MenuItem[] = [
    {
      name: "Spaghetti Carbonara",
      description: "Pancetta, egg yolk, pecorino, black pepper",
      price: "$22",
    },
    {
      name: "Lobster Ravioli",
      description: "House-made pasta, lobster filling, saffron cream",
      price: "$32",
    },
    {
      name: "Penne Arrabiata",
      description: "Spicy tomato sauce, garlic, red peppers",
      price: "$19",
    },
  ];

  const seafoodMeatItems: MenuItem[] = [
    {
      name: "Grilled Branzino",
      description: "Mediterranean sea bass, lemon, herbs, vegetables",
      price: "$28",
    },
    {
      name: "Osso Buco",
      description: "Braised veal shank, risotto milanese, gremolata",
      price: "$36",
    },
  ];

  const dessertItems: MenuItem[] = [
    {
      name: "Tiramisu",
      description: "Classic Italian dessert, mascarpone, espresso",
      price: "$12",
    },
    {
      name: "Panna Cotta",
      description: "Vanilla bean custard, berry compote",
      price: "$10",
    },
    {
      name: "Gelato Selection",
      description: "Vanilla, chocolate, pistachio, seasonal flavors",
      price: "$8",
    },
  ];

  const beverageItems: MenuItem[] = [
    {
      name: "Espresso",
      description: "Traditional Italian coffee",
      price: "$4",
    },
    { name: "House Wine", description: "Red or white, glass", price: "$9" },
    {
      name: "Limoncello",
      description: "Traditional Italian digestif",
      price: "$8",
    },
  ];

  const appIcon = AppIcon;

  return (
    <div
      className={`${styles.container} ${
        isLeftOpen && isRightOpen ? styles.darkened : ""
      }`}
    >
      <div className={styles.menuContainer} ref={menuContainerRef}>
        <div
          className={`${styles.menuWrapper} ${
            isLeftOpen && isRightOpen ? styles.opened : ""
          } ${isLeftOpen ? styles.leftOpened : ""} ${
            isRightOpen ? styles.rightOpened : ""
          }`}
          id="menuWrapper"
        >
          <Button
            classes={styles.openButton}
            label="Open Menu"
            onClick={openMenu}
          />

          {showCloseButton && (
            <ButtonIcon
              classes={styles.closeButton}
              icon={appIcon.CANCEL}
              color="background-light"
              iconColor="accent"
              onClick={closeMenu}
            />
          )}

          <div onClick={handlePanelClick}>
            <MenuPanel title="Appetizers" position="left">
              <MenuSection title="Starters" items={appetizerItems} />
              <MenuSection title="Salads" items={saladItems} />
            </MenuPanel>
          </div>

          <div onClick={handlePanelClick}>
            <MenuPanel title="Main Courses" position="center">
              <MenuSection title="Pasta" items={pastaItems} />
              <MenuSection title="Seafood & Meat" items={seafoodMeatItems} />
            </MenuPanel>
          </div>

          <div onClick={handlePanelClick}>
            <MenuPanel title="Desserts & Drinks" position="right">
              <MenuSection title="Dolci" items={dessertItems} />
              <MenuSection title="Beverages" items={beverageItems} />
            </MenuPanel>
          </div>
        </div>
        <div className={styles.holyLightOverlay}></div>
      </div>
    </div>
  );
};

export default RestaurantMenu;
